import { useCallback, useEffect, useRef } from 'react';
import { DevtoolPopupPosition } from '../types/store.types';

interface DraggableOptions {
  position: DevtoolPopupPosition;
  setPosition: (p: DevtoolPopupPosition) => void;
  getSize: () => { width: number; height: number };
}

/**
 * The callbacks (`setPosition`, `getSize`) and the position are read via refs
 * so the window-attached `pointermove`/`pointerup` handlers stay stable for
 * the whole drag — otherwise a re-render triggered by `setPosition` itself
 * would invalidate the listener before the next move.
 */
export function useDevtoolDraggable({
  position,
  setPosition,
  getSize,
}: DraggableOptions): {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
} {
  const dragRef = useRef<{
    offsetX: number;
    offsetY: number;
    pointerId: number;
    target: HTMLElement;
  } | null>(null);
  const positionRef = useRef(position);
  positionRef.current = position;
  const setPositionRef = useRef(setPosition);
  setPositionRef.current = setPosition;
  const getSizeRef = useRef(getSize);
  getSizeRef.current = getSize;

  const handlersRef = useRef<{
    move: (e: PointerEvent) => void;
    up: (e: PointerEvent) => void;
  } | null>(null);
  if (handlersRef.current === null) {
    const move = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) {
        return;
      }
      const size = getSizeRef.current();
      const maxX = Math.max(0, window.innerWidth - size.width);
      const maxY = Math.max(0, window.innerHeight - size.height);
      const x = Math.min(maxX, Math.max(0, e.clientX - drag.offsetX));
      const y = Math.min(maxY, Math.max(0, e.clientY - drag.offsetY));
      setPositionRef.current({ x, y });
    };
    const up = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) {
        return;
      }
      try {
        drag.target.releasePointerCapture(drag.pointerId);
      } catch {
        /* ignore */
      }
      dragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    handlersRef.current = { move, up };
  }

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const target = e.currentTarget;
    dragRef.current = {
      offsetX: e.clientX - positionRef.current.x,
      offsetY: e.clientY - positionRef.current.y,
      pointerId: e.pointerId,
      target,
    };
    try {
      target.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const { move, up } = handlersRef.current!;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }, []);

  useEffect(() => {
    return () => {
      const handlers = handlersRef.current;
      if (handlers) {
        window.removeEventListener('pointermove', handlers.move);
        window.removeEventListener('pointerup', handlers.up);
        window.removeEventListener('pointercancel', handlers.up);
      }
    };
  }, []);

  return { onPointerDown };
}
